import { styled, Tooltip, tooltipClasses, TooltipProps } from '@mui/material';

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 700,
    },
});

function decodeUnicodeEscapes(str: string): string {
    return str.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => {
        return String.fromCharCode(parseInt(hex, 16));
    });
}

function formatJson(jsonString: string): string {
    try {
        const parsed = JSON.parse(jsonString);

        if (!parsed.Data) {
            return "Error: No 'Data' property found in JSON";
        }

        if (parsed.OldData) {
            return formatComparison(parsed.Data, parsed.OldData);
        } else {
            return formatSimple(parsed.Data);
        }
    } catch (error) {
        return JSON.stringify(JSON.parse(jsonString), null, 2);
    }
}

function formatSimple(data: any): string {
    const lines: string[] = [];

    for (const [key, value] of Object.entries(data)) {
        if (isDictionary(value)) {
            lines.push(formatDictionarySimple(key, value));
        } else {
            lines.push(`${key}: ${formatValue(value)}`);
        }
    }

    return lines.join('\n');
}

function formatComparison(data: any, oldData: any): string {
    const lines: string[] = [];
    const allKeys = new Set([...Object.keys(data), ...Object.keys(oldData)]);

    for (const key of allKeys) {
        const hasOldValue = key in oldData;
        const hasNewValue = key in data;

        if (hasOldValue && hasNewValue) {
            const oldValue = oldData[key];
            const newValue = data[key];

            if (isDictionary(oldValue) && isDictionary(newValue)) {
                const dictionaryDiff = formatDictionaryComparison(key, newValue, oldValue);
                if (dictionaryDiff) {
                    lines.push(dictionaryDiff);
                }
            } else {
                const oldFormatted = formatValue(oldValue);
                const newFormatted = formatValue(newValue);

                if (oldFormatted !== newFormatted) {
                    lines.push(`${key}: ${oldFormatted} => ${newFormatted}`);
                }
            }
        } else if (hasNewValue) {
            lines.push(`${key}: (new) => ${formatValue(data[key])}`);
        } else if (hasOldValue) {
            lines.push(`${key}: ${formatValue(oldData[key])} => (removed)`);
        }
    }

    return lines.join('\n');
}

function isDictionary(value: any): boolean {
    return typeof value === 'object' && value !== null && !Array.isArray(value) && Object.keys(value).length > 0;
}

function formatDictionarySimple(key: string, dictionary: any): string {
    if (typeof dictionary !== 'object' || dictionary === null || Array.isArray(dictionary)) {
        return `${key}: ${formatValue(dictionary)}`;
    }

    const logsToPrint: string[] = [];

    for (const [lang, text] of Object.entries(dictionary)) {
        logsToPrint.push(`  [${lang}]: ${formatValue(text)}`);
    }

    return `${key}:\n${logsToPrint.join('\n')}`;
}

function formatDictionaryComparison(
    key: string,
    newDict: Record<string, string>,
    oldDict: Record<string, string>,
): string {
    const allLanguages = new Set([...Object.keys(newDict), ...Object.keys(oldDict)]);

    const logsToPrint: string[] = [];

    for (const lang of allLanguages) {
        const hasOldValue = lang in oldDict;
        const hasNewValue = lang in newDict;

        if (hasOldValue && hasNewValue) {
            if (oldDict[lang] !== newDict[lang]) {
                logsToPrint.push(
                    `  [${lang}]: "${decodeUnicodeEscapes(oldDict[lang])}" => "${decodeUnicodeEscapes(newDict[lang])}"`,
                );
            }
        } else if (hasNewValue) {
            logsToPrint.push(`  [${lang}]: (new) => "${decodeUnicodeEscapes(newDict[lang])}"`);
        } else if (hasOldValue) {
            logsToPrint.push(`  [${lang}]: "${decodeUnicodeEscapes(oldDict[lang])}" => (removed)`);
        }
    }

    if (logsToPrint.length === 0) {
        return '';
    }

    return `${key}:\n${logsToPrint.join('\n')}`;
}

function formatValue(value: any): string {
    if (typeof value === 'string') {
        const decodedValue = decodeUnicodeEscapes(value);
        return `"${decodedValue}"`;
    } else if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    } else if (value === null || value === undefined) {
        return 'null';
    } else if (Array.isArray(value)) {
        return `[${value.map(formatValue).join(', ')}]`;
    } else if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return String(value);
}

const JsonFormatCell = ({ value }: { value: string }): JSX.Element => {
    if (!value) return <></>;

    const formattedJson = formatJson(value);
    return (
        <CustomWidthTooltip
            placement="right"
            title={<pre style={{ fontSize: 15, whiteSpace: 'pre-wrap', overflow: 'auto' }}>{formattedJson}</pre>}
        >
            <pre>{formattedJson.substring(0, 50)}...</pre>
        </CustomWidthTooltip>
    );
};

export default JsonFormatCell;
